import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import LoginModal from '../components/auth/LoginModal';
import RegisterModal from '../components/auth/RegisterModal';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';

// Global controller for the auth modals so any component (header, cart drawer,
// checkout) can prompt the user to log in / register. Renders the modals once.
const AuthUIContext = createContext(null);

export function AuthUIProvider({ children }) {
  const [mode, setMode] = useState(null); // 'login' | 'register' | 'forgot' | null

  const openLogin = useCallback(() => setMode('login'), []);
  const openRegister = useCallback(() => setMode('register'), []);
  const openForgot = useCallback(() => setMode('forgot'), []);
  const close = useCallback(() => setMode(null), []);

  const value = useMemo(
    () => ({ mode, openLogin, openRegister, openForgot, close }),
    [mode, openLogin, openRegister, openForgot, close]
  );

  return (
    <AuthUIContext.Provider value={value}>
      {children}
      <LoginModal
        isOpen={mode === 'login'}
        onClose={close}
        onSwitchToRegister={openRegister}
        onSwitchToForgot={openForgot}
      />
      <RegisterModal isOpen={mode === 'register'} onClose={close} onSwitchToLogin={openLogin} />
      <ForgotPasswordModal isOpen={mode === 'forgot'} onClose={close} onSwitchToLogin={openLogin} />
    </AuthUIContext.Provider>
  );
}

export function useAuthUI() {
  const ctx = useContext(AuthUIContext);
  if (!ctx) {
    throw new Error('useAuthUI must be used within an AuthUIProvider');
  }
  return ctx;
}
